import React, { useState, useEffect } from 'react';
import { getPackagings, createPackaging, updatePackaging, deletePackaging, updateOrder, getPriceList } from '../../services/packagingService';
import { getProducts } from '../../services/productService';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Chip,
  Fade,
  Slide,
  LinearProgress,
  Skeleton,
  Stack,
  Tooltip,
  Avatar,
  alpha,
  useTheme
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  Add,
  Search,
  FilterList,
  GetApp,
  Refresh,
  Inventory2 as PackageIcon,
  MonetizationOn,
  TrendingUp,
  LocalShipping,
  Warning,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useSnackbar } from 'notistack';

const Packaging = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  
  const [packages, setPackages] = useState([]);
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [errors, setErrors] = useState({});
 
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [priceRangeFilter, setPriceRangeFilter] = useState({ min: '', max: '' });
  
  const [formData, setFormData] = useState({
    name: '',
    product: '',
    volume: '',
    units: 'g',
    price: '',
    income: '',
    courierPayment: '',
    courierPenalty: '',
    cities: ''
  });

  useEffect(() => {
    fetchPackages();
    fetchProducts();

  }, []);

  useEffect(() => {
    applyFilters();
  }, [packages, searchQuery, productFilter, priceRangeFilter]);

  const fetchPackages = async () => {
    try {
      const data = await getPackagings();
      const items = Array.isArray(data) ? data : (data?.data || []);
      setPackages(items);
    } catch (error) {
      console.error('Error fetching packages:', error);
      enqueueSnackbar('Ошибка при загрузке фасовок', { variant: 'error' });
    }
    setLoading(false);
  };

  const fetchProducts = async () => {
    try {
      const data = await getProducts();
      const items = Array.isArray(data) ? data : (data?.data || []);
      setProducts(items);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

 
  // Filtering functions
  const applyFilters = () => {
    let filtered = [...packages];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(pkg =>
        pkg.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (pkg.product && pkg.product.name && pkg.product.name.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Product filter
    if (productFilter) {
      filtered = filtered.filter(pkg => pkg.product && pkg.product._id === productFilter);
    }

    // Price range filter
    if (priceRangeFilter.min) {
      filtered = filtered.filter(pkg => parseFloat(pkg.price) >= parseFloat(priceRangeFilter.min));
    }
    if (priceRangeFilter.max) {
      filtered = filtered.filter(pkg => parseFloat(pkg.price) <= parseFloat(priceRangeFilter.max));
    }

    setFilteredPackages(filtered);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleProductFilter = (event) => {
    setProductFilter(event.target.value);
  };

  const handlePriceRangeChange = (field, value) => {
    setPriceRangeFilter(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setSearchQuery('');
    setProductFilter('');
    setPriceRangeFilter({ min: '', max: '' });
  };

  // Export to Excel
  const exportToExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();
      
      const wsData = [
        ['ID', 'Название', 'Товар', 'Объем', 'Единицы', 'Стоимость (₽)', 'Доход (₽)', 'Плата курьеру (₽)', 'Штраф курьеру (₽)'],
        ...filteredPackages.map(pkg => [
          pkg._id,
          pkg.name,
          pkg.product ? pkg.product.name : '',
          pkg.volume,
          pkg.units === 'g' ? 'г' : 'шт',
          pkg.price,
          pkg.income,
          pkg.courierPayment,
          pkg.courierPenalty
        ])
      ];

      const ws = XLSX.utils.aoa_to_sheet(wsData);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 25 }, { wch: 30 }, { wch: 25 }, { wch: 12 }, { wch: 12 },
        { wch: 15 }, { wch: 15 }, { wch: 18 }, { wch: 18 }
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Фасовки');
      
      const fileName = `Фасовки_${new Date().toLocaleDateString('ru-RU')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      enqueueSnackbar('Экспорт выполнен успешно', { variant: 'success' });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      enqueueSnackbar('Ошибка при экспорте', { variant: 'error' });
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setEditMode(false);
    setFormData({
      name: '',
      product: '',
      volume: '',
      units: 'g',
      price: '',
      income: '',
      courierPayment: '',
      courierPenalty: '',
      cities: ''
    });
  };

  const handleClose = () => {
    setOpen(false);
    setErrors({});
  };

  const handleSubmit = async () => {
    try {
      setErrors({});
      // ensure product is sent as ID
      const payload = {
        ...formData,
        product: formData.product?._id || formData.product
      };
      if (editMode) {
        await updatePackaging(formData._id, payload);
        enqueueSnackbar('Фасовка успешно обновлена', { variant: 'success' });
      } else {
        await createPackaging(payload);
        enqueueSnackbar('Фасовка успешно добавлена', { variant: 'success' });
      }
      await fetchPackages();
      handleClose();
    } catch (error) {
      console.error('Error saving package:', error);
      const message = error?.response?.data?.message || error?.response?.data?.error || 'Ошибка при сохранении фасовки';
      enqueueSnackbar(message, { variant: 'error' });
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (errorData.error && errorData.error.includes('validation failed')) {
          const validationErrors = {};
          const errorMessage = errorData.error;
          Object.keys(formData).forEach(field => {
            const fieldError = new RegExp(`${field}: (.*?)(?=(,|$))`).exec(errorMessage);
            if (fieldError) {
              validationErrors[field] = fieldError[1];
            }
          });
          setErrors(validationErrors);
        }
      }
    }
  };

  const handleEdit = (pkg) => {
    setFormData(pkg);
    setEditMode(true);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deletePackaging(id);
      await fetchPackages();
      enqueueSnackbar('Фасовка удалена', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting package:', error);
      enqueueSnackbar('Не удалось удалить фасовку', { variant: 'error' });
    }
  };

  const getProductName = (productId) => {
    const product = products.find(p => p._id === productId);
    return product ? product.name : '';
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(packages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setPackages(items);

    try {
      await updateOrder({
        items: items.map((item, index) => ({
          _id: item._id,
          order: index
        }))
      });
    } catch (error) {
      console.error('Error updating order:', error);
      fetchPackages(); // Revert to server state if error
    }
  };

  return (
    <Fade in={true} timeout={800}>
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        {/* Hero Section */}
        <Paper 
          elevation={4}
          sx={{ 
            bgcolor: 'background.paper',
            borderRadius: 3,
            p: 3,
            mb: 3,
            color: 'text.primary'
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <Avatar sx={{ bgcolor: 'action.selected', color: 'primary.main', mr: 2, width: 56, height: 56 }}>
                <PackageIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  Управление фасовками
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Настройка фасовок, цен и порядка отображения
                </Typography>
              </Box>
            </Box>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Экспорт в Excel">
                <IconButton 
                  onClick={exportToExcel}
                  disabled={filteredPackages.length === 0 || loading}
                  sx={{ 
                    color: 'text.primary',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': { bgcolor: 'action.selected', color: 'primary.main' },
                    '&:disabled': { opacity: 0.5 }
                  }}
                >
                  <GetApp />
                </IconButton>
              </Tooltip>
              <Tooltip title="Обновить данные">
                <IconButton 
                  onClick={fetchPackages}
                  disabled={loading}
                  sx={{ 
                    color: 'text.primary',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': { bgcolor: 'action.selected', color: 'primary.main' },
                    '&:disabled': { opacity: 0.5 }
                  }}
                >
                  {loading ? <LinearProgress size={20} sx={{ color: 'text.primary' }} /> : <Refresh />}
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </Paper>

        {/* Search and Filters */}
        <Paper elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 3, pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Search color="primary" />
                Поиск и фильтры
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  size="small" 
                  onClick={clearFilters}
                  startIcon={<FilterList />}
                  disabled={loading}
                >
                  Сбросить
                </Button>
                <Button 
                  variant="contained"
                  onClick={handleOpen}
                  startIcon={<Add />}
                  disabled={loading}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                >
                  Добавить фасовку
                </Button>
              </Box>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Поиск фасовок"
                  value={searchQuery}
                  onChange={handleSearch}
                  placeholder="Введите название фасовки или товара"
                  disabled={loading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3.5}>
                <FormControl fullWidth>
                  <InputLabel>Товар</InputLabel>
                  <Select
                    value={productFilter}
                    onChange={handleProductFilter}
                    label="Товар"
                    disabled={loading}
                    sx={{
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      }
                    }}
                  >
                    <MenuItem value="">Все товары</MenuItem>
                    {products.map((product) => (
                      <MenuItem key={product._id} value={product._id}>
                        {product.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={1.75}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Цена от"
                  type="number"
                  value={priceRangeFilter.min}
                  onChange={(e) => handlePriceRangeChange('min', e.target.value)}
                  placeholder="0"
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} md={1.75}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Цена до"
                  type="number"
                  value={priceRangeFilter.max}
                  onChange={(e) => handlePriceRangeChange('max', e.target.value)}
                  placeholder="∞"
                  disabled={loading}
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Statistics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {loading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                  <Skeleton variant="text" width={120} height={24} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width={80} height={36} />
                </Card>
              </Grid>
            ))
          ) : (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                  <Typography variant="body2" color="text.secondary">Всего фасовок</Typography>
                  <Typography variant="h4" fontWeight="bold">{packages.length}</Typography>
                  <Typography variant="caption" color="text.secondary">отфильтровано: {filteredPackages.length}</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                  <Typography variant="body2" color="text.secondary">Средняя стоимость</Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {packages.length > 0 ? Math.round(packages.reduce((s,p)=> s + (Number(p.price)||0), 0) / packages.length) : 0} ₽
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                  <Typography variant="body2" color="text.secondary">Сумма выплат курьерам</Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {packages.reduce((s,p)=> s + (Number(p.courierPayment)||0), 0)} ₽
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                  <Typography variant="body2" color="text.secondary">Сумма штрафов курьерам</Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {packages.reduce((s,p)=> s + (Number(p.courierPenalty)||0), 0)} ₽
                  </Typography>
                </Card>
              </Grid>
            </>
          )}
        </Grid>

        {/* Table */}
        <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight="bold">Список фасовок ({loading ? '...' : filteredPackages.length})</Typography>
            {!loading && filteredPackages.length === 0 && packages.length > 0 && (
              <Chip icon={<FilterList />} label="Нет результатов" color="warning" variant="outlined" />
            )}
          </Box>
          {loading && <LinearProgress />}

          {loading ? (
            <Box sx={{ p: 3 }}>
              {Array.from({ length: 5 }).map((_, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 2 }}>
                  <Skeleton variant="circular" width={24} height={24} sx={{ mr: 2 }} />
                  <Skeleton variant="text" width="30%" height={24} sx={{ mr: 2 }} />
                  <Skeleton variant="text" width="20%" height={24} sx={{ mr: 2 }} />
                  <Skeleton variant="text" width="10%" height={24} sx={{ mr: 2 }} />
                </Box>
              ))}
            </Box>
          ) : filteredPackages.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <PackageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {packages.length === 0 ? 'Нет фасовок' : 'Нет результатов'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {packages.length === 0 ? 'Добавьте первую фасовку для начала работы' : 'Попробуйте изменить параметры поиска'}
              </Typography>
              {packages.length === 0 && (
                <Button 
                  variant="contained" 
                  startIcon={<Add />}
                  onClick={handleOpen}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                >
                  Добавить первую фасовку
                </Button>
              )}
            </Box>
          ) : (
            <TableContainer>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                      <TableCell width={50} sx={{ fontWeight: 'bold' }}>
                        <DragIndicatorIcon color="action" />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Название</TableCell>
                      <TableCell width={140} sx={{ fontWeight: 'bold' }}>Товар</TableCell>
                      <TableCell width={120} sx={{ fontWeight: 'bold' }}>Объем</TableCell>
                      <TableCell width={120} sx={{ fontWeight: 'bold' }}>Стоимость</TableCell>
                      <TableCell width={120} sx={{ fontWeight: 'bold' }}>Доход</TableCell>
                      <TableCell width={160} sx={{ fontWeight: 'bold' }}>Плата курьеру</TableCell>
                      <TableCell width={160} sx={{ fontWeight: 'bold' }}>Штраф курьеру</TableCell>
                      <TableCell width={150} align="right" sx={{ fontWeight: 'bold' }}>Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <Droppable droppableId="packages">
                    {(provided) => (
                      <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                        {filteredPackages.map((pkg, index) => (
                          <Draggable key={pkg._id} draggableId={pkg._id.toString()} index={index}>
                            {(provided) => (
                              <TableRow ref={provided.innerRef} {...provided.draggableProps}>
                                <TableCell>
                                  <Box {...provided.dragHandleProps}>
                                    <DragIndicatorIcon />
                                  </Box>
                                </TableCell>
                                <TableCell>{pkg.name}</TableCell>
                                <TableCell>{getProductName(pkg.product?._id || pkg.product)}</TableCell>
                                <TableCell>{`${pkg.volume} ${pkg.units}`}</TableCell>
                                <TableCell>{pkg.price} ₽</TableCell>
                                <TableCell>{pkg.income} ₽</TableCell>
                                <TableCell>{pkg.courierPayment} ₽</TableCell>
                                <TableCell>{pkg.courierPenalty} ₽</TableCell>
                                <TableCell align="right">
                                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    <Tooltip title="Редактировать">
                                      <IconButton onClick={() => handleEdit(pkg)} size="small">
                                        <EditIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Удалить">
                                      <IconButton onClick={() => handleDelete(pkg._id)} size="small" color="error">
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </TableBody>
                    )}
                  </Droppable>
                </Table>
              </DragDropContext>
            </TableContainer>
          )}
        </Paper>

        {/* Dialog */}
        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>{editMode ? 'Редактировать фасовку' : 'Добавить фасовку'}</DialogTitle>
          <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Название должно быть уникально для всего списка фасовок данного товара</Typography>
            <TextField
              margin="dense"
              label="Название"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={!!errors.name}
              helperText={errors.name}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth margin="dense">
              <InputLabel>Товар</InputLabel>
              <Select
                value={formData.product?._id || formData.product || ''}
                onChange={(e) => setFormData({ ...formData, product: e.target.value })}
                label="Товар"
              >
                {products.map((product) => (
                  <MenuItem key={product._id} value={product._id}>
                    {product.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              margin="dense"
              label="Объем"
              fullWidth
              value={formData.volume}
              onChange={(e) => setFormData({ ...formData, volume: e.target.value })}
              error={!!errors.volume}
              helperText={errors.volume}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>Единицы</InputLabel>
              <Select
                value={formData.units || ''}
                onChange={(e) => setFormData({ ...formData, units: e.target.value })}
                label="Единицы"
              >
                <MenuItem value="g">г</MenuItem>
                <MenuItem value="st">штуки</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl fullWidth margin="dense" error={!!errors.price}>
       
               <TextField
              margin="dense"
              label="Стоимость ₽"
              fullWidth
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              error={!!errors.price}
              helperText={errors.price}
            />
              {errors.price && (
                <Typography variant="caption" color="error">
                  {errors.price}
                </Typography>
              )}
            </FormControl>

          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              margin="dense"
              label="Доход ₽"
              fullWidth
              value={formData.income}
              onChange={(e) => setFormData({ ...formData, income: e.target.value })}
              error={!!errors.income}
              helperText={errors.income}
            />
            <TextField
              margin="dense"
              label="Плата курьеру ₽"
              fullWidth
              value={formData.courierPayment}
              onChange={(e) => setFormData({ ...formData, courierPayment: e.target.value })}
              error={!!errors.courierPayment}
              helperText={errors.courierPayment}
            />
            <TextField
              margin="dense"
              label="Штраф курьеру ₽"
              fullWidth
              value={formData.courierPenalty}
              onChange={(e) => setFormData({ ...formData, courierPenalty: e.target.value })}
              error={!!errors.courierPenalty}
              helperText={errors.courierPenalty}
            />
          </Box>

        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Отмена</Button>
          <Button onClick={handleSubmit} color="primary">
            {editMode ? 'Сохранить' : 'Добавить'}
          </Button>
        </DialogActions>
        </Dialog>
      </Container>
    </Fade>
  );
};

export default Packaging;
